import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EditIcon, ChevronDownIcon, NavbarIcon, LeftSidebarIcon, RightSidebarIcon } from "./icons";
import { API_ENDPOINTS } from "./constants";
import { generateEditUrl } from '../../core/utils/urlUtils';
import { normalizeSubdomain } from "../../core/utils/subdomainUtils";
import logger from '../../core/utils/logger';
import { apiFetch } from "../../core/utils/api";
import { useEdit } from "../context/EditContext";
import { useEditUI } from "../context/EditUIPreferencesContext";
import { Z_INDEX } from "../../core/utils/zIndexScale";
import { typography, surfaces, borders, textColors, brandColors, interactive } from "../../design-system/tokens";

const PageEditorDropdown = ({ 
  currentPage, 
  website, 
  websiteId, 
  username, 
  domain, 
  version_slug,
  isHighlighted = false,
  isLeftEdge = false,
  isRightEdge = false
}) => {
  const { selectElement } = useEdit() || {};
  const { setEditPanelView } = useEditUI() || {};
  const [isOpen, setIsOpen] = useState(false);
  const [pageData, setPageData] = useState({
    name: '',
    subdomain: '',
    inNavbar: false,
    inLeftSidebar: false,
    inRightSidebar: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (currentPage) {
      logger.info(`[PAGE_EDIT_INIT] ========== PAGE EDITOR INITIALIZATION ==========`);
      logger.info(`[PAGE_EDIT_INIT] Current page: ${currentPage?.name} (ID: ${currentPage?.id})`);
      logger.info(`[PAGE_EDIT_INIT] Website object:`, website);
      logger.info(`[PAGE_EDIT_INIT] Website navbar:`, website?.navbar);
      logger.info(`[PAGE_EDIT_INIT] Website navbar ID:`, website?.navbar?.id);
      logger.info(`[PAGE_EDIT_INIT] Website navbar type:`, typeof website?.navbar?.id);
      logger.info(`[PAGE_EDIT_INIT] Website navbar sections:`, website?.navbar?.sections);
      
      // Log the raw navbar object properties
      if (website?.navbar) {
        logger.info(`[PAGE_EDIT_INIT] Navbar object keys:`, Object.keys(website.navbar));
        logger.info(`[PAGE_EDIT_INIT] Navbar object full structure:`, JSON.stringify(website.navbar, null, 2));
      }
      
      if (website?.navbar?.sections) {
        website.navbar.sections.forEach((section, index) => {
          logger.info(`[PAGE_EDIT_INIT] Section ${index}:`, {
            id: section.id,
            link_choice: section.link_choice,
            link_internal: section.link_internal,
            label: section.label
          });
        });
      }
      
      // Check if navbar sections point to this page using the correct field name
      // link_internal is serialized as a page object, so we need to check the id property
      const inNavbar = website?.navbar?.sections?.some(section => {
        const matches = section.link_choice === 'internal' && section.link_internal?.id === currentPage.id;
        logger.info(`[PAGE_EDIT_INIT] Section ${section.id} matches page ${currentPage.id}? ${matches}`, {
          link_choice: section.link_choice,
          link_internal_id: section.link_internal?.id,
          current_page_id: currentPage.id
        });
        return matches;
      }) || false;
      
      // Check if left sidebar sections point to this page  
      const inLeftSidebar = website?.left_sidebar?.sections?.some(section => 
        section.link_choice === 'internal' && section.link_internal?.id === currentPage.id
      ) || false;
      
      // Check if right sidebar sections point to this page
      const inRightSidebar = website?.right_sidebar?.sections?.some(section => 
        section.link_choice === 'internal' && section.link_internal?.id === currentPage.id
      ) || false;

      logger.info(`[PAGE_EDIT_INIT] Detection results - inNavbar: ${inNavbar}, inLeftSidebar: ${inLeftSidebar}, inRightSidebar: ${inRightSidebar}`);

      setPageData({
        name: currentPage.name || '',
        subdomain: currentPage.subdomain || '',
        inNavbar,
        inLeftSidebar,
        inRightSidebar
      });
    }
  }, [currentPage, website]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleNavigationUpdates = async () => {
    logger.info(`[PAGE_EDIT_NAV] ========== NAVIGATION UPDATES START ==========`);
    logger.info(`[PAGE_EDIT_NAV] Current page: ${currentPage?.name} (ID: ${currentPage?.id})`);
    logger.info(`[PAGE_EDIT_NAV] Page data: ${JSON.stringify(pageData)}`);
    logger.info(`[PAGE_EDIT_NAV] Website navigation state - hasNavbar: ${!!website?.navbar}, hasLeftSidebar: ${!!website?.left_sidebar}, hasRightSidebar: ${!!website?.right_sidebar}`);
    
    // Check current state using correct field names
    // link_internal is serialized as a page object, so we need to check the id property
    const currentInNavbar = website?.navbar?.sections?.some(section => 
      section.link_choice === 'internal' && section.link_internal?.id === currentPage.id
    ) || false;
    
    const currentInLeftSidebar = website?.left_sidebar?.sections?.some(section => 
      section.link_choice === 'internal' && section.link_internal?.id === currentPage.id
    ) || false;
    
    const currentInRightSidebar = website?.right_sidebar?.sections?.some(section => 
      section.link_choice === 'internal' && section.link_internal?.id === currentPage.id
    ) || false;

    logger.info(`[PAGE_EDIT_NAV] Current navigation states - inNavbar: ${currentInNavbar}, inLeftSidebar: ${currentInLeftSidebar}, inRightSidebar: ${currentInRightSidebar}`);
    logger.info(`[PAGE_EDIT_NAV] Desired navigation states - inNavbar: ${pageData.inNavbar}, inLeftSidebar: ${pageData.inLeftSidebar}, inRightSidebar: ${pageData.inRightSidebar}`);

    const navigationPromises = [];

    if (website?.navbar && pageData.inNavbar !== currentInNavbar) {
      logger.info(`[PAGE_EDIT_NAV] Navbar state change detected: ${currentInNavbar} → ${pageData.inNavbar}`);
      logger.info(`[PAGE_EDIT_NAV] Navbar object:`, website.navbar);
      logger.info(`[PAGE_EDIT_NAV] Navbar ID:`, website.navbar.id);
      
      if (!website.navbar.id) {
        logger.error(`[PAGE_EDIT_NAV] Navbar ID is undefined! Cannot proceed with navbar operations.`);
        throw new Error('Navbar ID is undefined - cannot update navigation');
      }
      
      if (pageData.inNavbar) {
        // Create navbar section with correct field names
        const navbarData = {
          label: { content: pageData.name, type: "text" },
          link_choice: "internal",
          link_internal: currentPage.id,
          order: (website.navbar.sections?.length || 0) + 1,
        };
        logger.info(`[PAGE_EDIT_NAV] Creating navbar section with data: ${JSON.stringify(navbarData)}`);
        logger.info(`[PAGE_EDIT_NAV] API endpoint: ${API_ENDPOINTS.NAVBAR_SECTIONS_CREATE(website.navbar.id)}`);
        
        const navbarPromise = apiFetch(API_ENDPOINTS.NAVBAR_SECTIONS_CREATE(website.navbar.id), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(navbarData),
        }).then(async (response) => {
          logger.info(`[PAGE_EDIT_NAV] Navbar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const responseText = await response.text();
            logger.error(`[PAGE_EDIT_NAV] Navbar section creation failed - Response text: ${responseText}`);
            
            // Try to parse as JSON, but handle HTML error pages
            let errorData;
            try {
              errorData = JSON.parse(responseText);
            } catch (e) {
              throw new Error(`Navbar section creation failed with status ${response.status}: ${responseText.substring(0, 200)}...`);
            }
            
            throw new Error(`Navbar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.info(`[PAGE_EDIT_NAV] Navbar section created successfully: ${JSON.stringify(responseData)}`);
            return responseData;
          }
        });
        
        navigationPromises.push(navbarPromise);
      } else {
        // Find and delete navbar section using correct field names
        const section = website.navbar.sections?.find(s => 
          s.link_choice === 'internal' && s.link_internal?.id === currentPage.id
        );
        if (section) {
          logger.info(`[PAGE_EDIT_NAV] Deleting navbar section: ID=${section.id}`);
          logger.info(`[PAGE_EDIT_NAV] Delete API endpoint: ${API_ENDPOINTS.NAVBAR_SECTIONS_DELETE(website.navbar.id, section.id)}`);
          
          const deletePromise = apiFetch(API_ENDPOINTS.NAVBAR_SECTIONS_DELETE(website.navbar.id, section.id), {
            method: 'DELETE',
            credentials: 'include',
          }).then(async (response) => {
            logger.info(`[PAGE_EDIT_NAV] Navbar section deletion response status: ${response.status}`);
            
            if (!response.ok) {
              const responseText = await response.text();
              logger.error(`[PAGE_EDIT_NAV] Navbar section deletion failed - Response text: ${responseText}`);
              
              // Try to parse as JSON, but handle HTML error pages
              let errorData;
              try {
                errorData = JSON.parse(responseText);
              } catch (e) {
                throw new Error(`Navbar section deletion failed with status ${response.status}: ${responseText.substring(0, 200)}...`);
              }
              
              throw new Error(`Navbar section deletion failed: ${JSON.stringify(errorData)}`);
            } else {
              logger.info(`[PAGE_EDIT_NAV] Navbar section deleted successfully`);
            }
          });
          
          navigationPromises.push(deletePromise);
        } else {
          logger.warning(`[PAGE_EDIT_NAV] No navbar section found to delete for page ${currentPage.id}`);
        }
      }
    }

    if (website?.left_sidebar && pageData.inLeftSidebar !== currentInLeftSidebar) {
      logger.info(`[PAGE_EDIT_NAV] Left sidebar state change detected: ${currentInLeftSidebar} → ${pageData.inLeftSidebar}`);
      
      if (pageData.inLeftSidebar) {
        // Create left sidebar section with correct field names
        const leftSidebarData = {
          label: { content: pageData.name, type: "text" },
          link_choice: "internal",
          link_internal: currentPage.id,
          order: (website.left_sidebar.sections?.length || 0) + 1,
        };
        logger.info(`[PAGE_EDIT_NAV] Creating left sidebar section with data: ${JSON.stringify(leftSidebarData)}`);
        
        const leftSidebarPromise = apiFetch(API_ENDPOINTS.LEFTSIDEBAR_SECTIONS_CREATE(website.left_sidebar.id), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(leftSidebarData),
        }).then(async (response) => {
          logger.info(`[PAGE_EDIT_NAV] Left sidebar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            logger.error(`[PAGE_EDIT_NAV] Left sidebar section creation failed: ${JSON.stringify(errorData)}`);
            throw new Error(`Left sidebar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.info(`[PAGE_EDIT_NAV] Left sidebar section created successfully: ${JSON.stringify(responseData)}`);
            return responseData;
          }
        });
        
        navigationPromises.push(leftSidebarPromise);
      } else {
        // Find and delete left sidebar section using correct field names
        const section = website.left_sidebar.sections?.find(s => 
          s.link_choice === 'internal' && s.link_internal?.id === currentPage.id
        );
        if (section) {
          logger.info(`[PAGE_EDIT_NAV] Deleting left sidebar section: ID=${section.id}`);
          
          const deletePromise = apiFetch(API_ENDPOINTS.LEFTSIDEBAR_SECTIONS_DELETE(website.left_sidebar.id, section.id), {
            method: 'DELETE',
            credentials: 'include',
          }).then(async (response) => {
            logger.info(`[PAGE_EDIT_NAV] Left sidebar section deletion response status: ${response.status}`);
            
            if (!response.ok) {
              const errorData = await response.json();
              logger.error(`[PAGE_EDIT_NAV] Left sidebar section deletion failed: ${JSON.stringify(errorData)}`);
              throw new Error(`Left sidebar section deletion failed: ${JSON.stringify(errorData)}`);
            } else {
              logger.info(`[PAGE_EDIT_NAV] Left sidebar section deleted successfully`);
            }
          });
          
          navigationPromises.push(deletePromise);
        } else {
          logger.warning(`[PAGE_EDIT_NAV] No left sidebar section found to delete for page ${currentPage.id}`);
        }
      }
    }

    if (website?.right_sidebar && pageData.inRightSidebar !== currentInRightSidebar) {
      logger.info(`[PAGE_EDIT_NAV] Right sidebar state change detected: ${currentInRightSidebar} → ${pageData.inRightSidebar}`);
      
      if (pageData.inRightSidebar) {
        // Create right sidebar section with correct field names
        const rightSidebarData = {
          label: { content: pageData.name, type: "text" },
          link_choice: "internal", 
          link_internal: currentPage.id,
          order: (website.right_sidebar.sections?.length || 0) + 1,
        };
        logger.info(`[PAGE_EDIT_NAV] Creating right sidebar section with data: ${JSON.stringify(rightSidebarData)}`);
        
        const rightSidebarPromise = apiFetch(API_ENDPOINTS.RIGHTSIDEBAR_SECTIONS_CREATE(website.right_sidebar.id), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(rightSidebarData),
        }).then(async (response) => {
          logger.info(`[PAGE_EDIT_NAV] Right sidebar section creation response status: ${response.status}`);
          
          if (!response.ok) {
            const errorData = await response.json();
            logger.error(`[PAGE_EDIT_NAV] Right sidebar section creation failed: ${JSON.stringify(errorData)}`);
            throw new Error(`Right sidebar section creation failed: ${JSON.stringify(errorData)}`);
          } else {
            const responseData = await response.json();
            logger.info(`[PAGE_EDIT_NAV] Right sidebar section created successfully: ${JSON.stringify(responseData)}`);
            return responseData;
          }
        });
        
        navigationPromises.push(rightSidebarPromise);
      } else {
        // Find and delete right sidebar section using correct field names
        const section = website.right_sidebar.sections?.find(s => 
          s.link_choice === 'internal' && s.link_internal?.id === currentPage.id
        );
        if (section) {
          logger.info(`[PAGE_EDIT_NAV] Deleting right sidebar section: ID=${section.id}`);
          
          const deletePromise = apiFetch(API_ENDPOINTS.RIGHTSIDEBAR_SECTIONS_DELETE(website.right_sidebar.id, section.id), {
            method: 'DELETE',
            credentials: 'include',
          }).then(async (response) => {
            logger.info(`[PAGE_EDIT_NAV] Right sidebar section deletion response status: ${response.status}`);
            
            if (!response.ok) {
              const errorData = await response.json();
              logger.error(`[PAGE_EDIT_NAV] Right sidebar section deletion failed: ${JSON.stringify(errorData)}`);
              throw new Error(`Right sidebar section deletion failed: ${JSON.stringify(errorData)}`);
            } else {
              logger.info(`[PAGE_EDIT_NAV] Right sidebar section deleted successfully`);
            }
          });
          
          navigationPromises.push(deletePromise);
        } else {
          logger.warning(`[PAGE_EDIT_NAV] No right sidebar section found to delete for page ${currentPage.id}`);
        }
      }
    }

    // Wait for all navigation operations to complete
    if (navigationPromises.length > 0) {
      logger.info(`[PAGE_EDIT_NAV] Waiting for ${navigationPromises.length} navigation operations...`);
      const results = await Promise.allSettled(navigationPromises);
      
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        logger.error(`[PAGE_EDIT_NAV] ${failures.length} navigation operations failed:`, failures);
        throw new Error(`Navigation update failed: ${failures.map(f => f.reason?.message || 'Unknown error').join('; ')}`);
      } else {
        logger.info(`[PAGE_EDIT_NAV] All navigation operations completed successfully`);
      }
    } else {
      logger.info(`[PAGE_EDIT_NAV] No navigation changes needed`);
    }
    
    logger.info(`[PAGE_EDIT_NAV] ========== NAVIGATION UPDATES COMPLETE ==========`);
  };

  const handleSave = async () => {
    logger.info(`[PAGE_EDIT_SAVE] ========== PAGE SAVE START ==========`);
    logger.info(`[PAGE_EDIT_SAVE] Current page: ${currentPage?.name} (ID: ${currentPage?.id})`);
    logger.info(`[PAGE_EDIT_SAVE] Page data to save: ${JSON.stringify(pageData)}`);
    
    if (!currentPage || !pageData.name.trim()) {
      logger.error(`[PAGE_EDIT_SAVE] Validation failed - missing page or name`);
      setError('Page name is required');
      return;
    }

    setIsSaving(true);
    setError('');
    logger.info(`[PAGE_EDIT_SAVE] Starting save process...`);

    try {
      const normalizedSubdomain = normalizeSubdomain(pageData.subdomain);

      logger.info(`[PAGE_EDIT_SAVE] Making page update API call: ${API_ENDPOINTS.PAGE_UPDATE(websiteId, currentPage.id)}`);
      const pageResponse = await apiFetch(API_ENDPOINTS.PAGE_UPDATE(websiteId, currentPage.id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: pageData.name.trim(),
          subdomain: normalizedSubdomain,
        }),
      });

      logger.info(`[PAGE_EDIT_SAVE] Page update response status: ${pageResponse.status}`);

      if (!pageResponse.ok) {
        const errorData = await pageResponse.json();
        logger.error(`[PAGE_EDIT_SAVE] Page update failed: ${JSON.stringify(errorData)}`);
        
        // Handle validation errors with specific field information
        // DRF returns validation errors as arrays, so we need to extract the first message
        if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          throw new Error(errorData.non_field_errors[0]);
        } else if (errorData.non_field_errors) {
          throw new Error(errorData.non_field_errors);
        } else if (errorData.subdomain && Array.isArray(errorData.subdomain)) {
          throw new Error(errorData.subdomain[0]);
        } else if (errorData.subdomain) {
          throw new Error(errorData.subdomain);
        } else if (errorData.name && Array.isArray(errorData.name)) {
          throw new Error(errorData.name[0]);
        } else if (errorData.name) {
          throw new Error(errorData.name);
        } else {
          throw new Error(errorData.error || 'Failed to update page');
        }
      }

      const pageResponseData = await pageResponse.json();
      logger.info(`[PAGE_EDIT_SAVE] Page updated successfully: ${JSON.stringify(pageResponseData)}`);

      logger.info(`[PAGE_EDIT_SAVE] Starting navigation updates...`);
      await handleNavigationUpdates();
      logger.info(`[PAGE_EDIT_SAVE] Navigation updates completed`);
      
      setIsOpen(false);
      
      if (normalizedSubdomain !== currentPage.subdomain) {
        // Use the utility function to handle URL generation properly (handles empty subdomain correctly)
        const newUrl = generateEditUrl(username, domain, version_slug, normalizedSubdomain);
        logger.info(`[PAGE_EDIT_SAVE] Subdomain changed, redirecting to: ${newUrl}`);
        window.location.href = newUrl;
      } else {
        logger.info(`[PAGE_EDIT_SAVE] No subdomain change, reloading page`);
        window.location.reload();
      }

    } catch (error) {
      logger.error(`[PAGE_EDIT_SAVE] Save process failed: ${error.message}`);
      logger.error(`[PAGE_EDIT_SAVE] Full error:`, error);
      setError(error.message || 'Failed to update page');
    } finally {
      setIsSaving(false);
      logger.info(`[PAGE_EDIT_SAVE] ========== PAGE SAVE END ==========`);
    }
  };

  const handleDelete = async () => {
    if (!currentPage) return;
    if (!currentPage.subdomain) {
      setError('The main page cannot be deleted.');
      return;
    }
    setDeleting(true);
    try {
      const response = await apiFetch(API_ENDPOINTS.PAGE_DELETE(websiteId, currentPage.subdomain), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || 'Failed to delete page');
      }
      setIsOpen(false);
      const url = generateEditUrl(username, domain, version_slug, '');
      window.location.href = url;
    } catch (e) {
      setError(e.message || 'Failed to delete page');
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  if (!currentPage) return null;

  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs rounded-lg sm:text-sm font-medium transition-colors ${
          isHighlighted ? `bg-gradient-to-r from-blue-600 to-sky-500 dark:from-dark-950 dark:to-dark-900 text-white shadow-md hover:from-blue-700 hover:to-sky-600` : `${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
        } ${
          isLeftEdge ? 'rounded-l-lg' : ''
        } ${
          isRightEdge ? 'rounded-r-lg' : ''
        }`}
      >
        <EditIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
        <span className="ml-1 sm:ml-2 truncate max-w-20 sm:max-w-32">
          {currentPage.name || currentPage.subdomain || 'Untitled Page'}
        </span>
        <ChevronDownIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 ml-0.5 sm:ml-1 flex-shrink-0" />
      </button>
      
      {isOpen && (
        <div className={`absolute top-full mt-2 w-[min(24rem,calc(100vw-24px))] sm:w-96 ${surfaces.primary} rounded-lg shadow-xl border ${borders.default} left-1/2 transform -translate-x-1/2`} style={{ zIndex: Z_INDEX.DROPDOWN }}>
          <div className="p-4">
            {/* Header with pencil aligned to the top-right inside the box */}
            <div className="flex items-center justify-between mb-4">
              <h3 className={`${typography.h4} ${textColors.primary}`}>Edit Page</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // If website-level animations are open, switch back to the normal editor so
                  // page settings are not hidden behind WebsiteAnimationsPanel.
                  setEditPanelView?.("elements");
                  if (selectElement) {
                    selectElement({
                      ...currentPage,
                      type: "website_page",
                      website: websiteId,
                    });
                  } else {
                    logger.warn("Edit context not available to select website_page");
                  }
                  // Close the dropdown after opening the edit panel
                  setIsOpen(false);
                }}
                className={`w-7 h-7 rounded-full ${brandColors.primaryBg} text-white flex items-center justify-center shadow ${brandColors.primaryBgHover}`}
                title="Edit page properties"
                aria-label="Edit page properties"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{error}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-page-name-input" className={`${typography.label} ${textColors.primary} mb-1`}>
                  Page Name
                </label>
                <input
                  id="edit-page-name-input"
                  type="text"
                  value={pageData.name}
                  onChange={(e) => setPageData(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border ${borders.default} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-transparent ${typography.body} ${surfaces.primary} ${textColors.primary}`}
                  placeholder="About Us"
                />
              </div>
              
              <div>
                <label htmlFor="edit-page-subdomain-input" className={`${typography.label} ${textColors.primary} mb-1`}>
                  Subdomain
                </label>
                <p className={`${typography.caption} ${textColors.secondary} mb-2`}>
                  If left empty, this becomes the main page of your website
                </p>
                <div className="flex">
                  <span className={`inline-flex items-center px-3 rounded-l-md border border-r-0 ${borders.default} ${surfaces.secondary} ${textColors.secondary} ${typography.bodySmall}`}>
                    /
                  </span>
                  <input
                    id="edit-page-subdomain-input"
                    type="text"
                    value={pageData.subdomain}
                    // Allow user to type any chars; normalize on blur/save.
                    onChange={(e) => setPageData(prev => ({ ...prev, subdomain: e.target.value }))}
                    onBlur={() => setPageData(prev => ({ ...prev, subdomain: normalizeSubdomain(prev.subdomain) }))}
                    className={`flex-1 px-3 py-2 border ${borders.default} rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-transparent ${typography.body} ${surfaces.primary} ${textColors.primary}`}
                    placeholder="about"
                  />
                </div>
              </div>

              {(website?.navbar || website?.left_sidebar || website?.right_sidebar) && (
                <div className="space-y-3">
                  {website?.navbar && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3 flex items-center text-blue-600 dark:text-blue-600 justify-center w-5 h-5">
                          <NavbarIcon className="w-4 h-4" />
                        </div>
                        <span className={`${typography.bodySmall} ${textColors.primary}`}>Show in Navbar</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pageData.inNavbar}
                          onChange={(e) => setPageData(prev => ({ ...prev, inNavbar: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
                  
                  {website?.left_sidebar && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3 flex items-center text-blue-600 dark:text-blue-600 justify-center w-5 h-5">
                          <LeftSidebarIcon className="w-4 h-4" />
                        </div>
                        <span className={`${typography.bodySmall} ${textColors.primary}`}>Show in Left Sidebar</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pageData.inLeftSidebar}
                          onChange={(e) => setPageData(prev => ({ ...prev, inLeftSidebar: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
                  
                  {website?.right_sidebar && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3 flex items-center text-blue-600 dark:text-blue-600 justify-center w-5 h-5">
                          <RightSidebarIcon className="w-4 h-4" />
                        </div>
                        <span className={`${typography.bodySmall} ${textColors.primary}`}>Show in Right Sidebar</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pageData.inRightSidebar}
                          onChange={(e) => setPageData(prev => ({ ...prev, inRightSidebar: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-6 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={!currentPage?.subdomain}
                className={`p-2 ${surfaces.primary} ${textColors.secondary} hover:bg-red-500 hover:text-white dark:hover:bg-red-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                title="Delete Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                  />
                </svg>
              </button>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-2 ${typography.button} ${textColors.secondary} ${textColors.hover}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`px-4 py-2 ${brandColors.primaryBg} text-white ${typography.button} rounded-md ${brandColors.primaryBgHover} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    
    {/* Delete confirmation modal - render via portal to escape parent positioning */}
    {deleteConfirmOpen && createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30"
          style={{ 
            zIndex: Z_INDEX.MODAL_BACKDROP,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            padding: 0
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className={`relative w-full max-w-md ${surfaces.primary} rounded-xl shadow-2xl overflow-hidden mx-4`}
            style={{ margin: '0 1rem' }}
          >
            <div className={`flex items-center justify-between px-5 py-4 ${brandColors.primaryBg}`}>
              <h3 className={typography.h4Dark}>Delete page</h3>
              <button
                aria-label="Close"
                className="text-white/90 hover:text-white"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="px-5 py-5">
              <p className={`${typography.body} ${textColors.primary}`}>Are you sure you want to delete this page?</p>
            </div>
            <div className={`px-5 py-4 flex items-center justify-end space-x-3 border-t ${borders.default}`}>
              <button
                className={`px-4 py-2 rounded-md ${surfaces.tertiary} ${textColors.primary} border ${borders.default} ${surfaces.hover} ${typography.button} transition-colors`}
                onClick={() => setDeleteConfirmOpen(false)}
              >
                No
              </button>
              <button
                className={`px-4 py-2 rounded-md ${interactive.dangerBg} ${interactive.dangerHover} ${interactive.dangerText} disabled:opacity-50 ${typography.button} transition-colors`}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, delete'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body
    )}
    </>
  );
};

export default PageEditorDropdown; 